import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  LinearProgress,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  LocalGasStation as GasIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { plantsApi, aziendeApi } from '../../services/api';

const PlantForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    plant_code: '',
    delivery_code: '',
    nome: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    latitude: '',
    longitude: '',
    capacita_totale: '',
    current_level_liters: '',
    low_level_threshold: 15,
    initial_level_percent: '',
    initial_level_date: '',
    note: '',
    attivo: true,
    azienda_id: '',
  });

  const [errors, setErrors] = useState({});

  // Query per caricare impianto esistente
  const { data: plantData, isLoading: loadingPlant } = useQuery({
    queryKey: ['plant', id],
    queryFn: async () => {
      const response = await plantsApi.getById(id);
      return response.data?.data;
    },
    enabled: isEdit,
  });
  // Query per aziende
  const { data: aziendeData } = useQuery({
    queryKey: ['aziende-for-plant'],
    queryFn: async () => {
      const response = await aziendeApi.getAll();
      return response.data?.data || [];
    },
  });

  // Popola form quando i dati sono caricati
  useEffect(() => {
    if (plantData) {
      setFormData({
        plant_code: plantData.plant_code || '',
        delivery_code: plantData.delivery_code || '',
        nome: plantData.nome || '',
        indirizzo: plantData.indirizzo || '',
        cap: plantData.cap || '',
        citta: plantData.citta || '',
        provincia: plantData.provincia || '',
        latitude: plantData.latitude || '',
        longitude: plantData.longitude || '',
        capacita_totale: plantData.capacita_totale || '',
        current_level_liters: plantData.current_level_liters || '',
        low_level_threshold: plantData.low_level_threshold || 15,
        initial_level_percent: plantData.initial_level_percent || '',
        initial_level_date: plantData.initial_level_date ? plantData.initial_level_date.split('T')[0] : '',
        note: plantData.note || '',
        attivo: plantData.attivo !== false,
        azienda_id: plantData.azienda_id || '',
      });
    }
  }, [plantData]);

  // Mutation per creare/aggiornare
  const mutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return plantsApi.update(id, data);
      } else {
        return plantsApi.create(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plants']);
      if (isEdit) {
        queryClient.invalidateQueries(['plant', id]);
      }
      navigate('/crm/impianti');
    },
    onError: (error) => {
      console.error('Errore salvataggio:', error);
      setErrors({ submit: error.response?.data?.message || 'Errore nel salvataggio' });
    },
  });

  // Handler cambio campo
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Rimuovi errore per questo campo
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };
  // Validazione
  const validate = () => {
    const newErrors = {};
    
    if (!formData.plant_code?.trim()) {
      newErrors.plant_code = 'Codice impianto obbligatorio';
    }
    
    if (formData.capacita_totale && isNaN(parseFloat(formData.capacita_totale))) {
      newErrors.capacita_totale = 'Deve essere un numero';
    }
    
    if (formData.current_level_liters && isNaN(parseFloat(formData.current_level_liters))) {
      newErrors.current_level_liters = 'Deve essere un numero';
    }
    
    if (formData.low_level_threshold && (isNaN(parseFloat(formData.low_level_threshold)) || formData.low_level_threshold < 0 || formData.low_level_threshold > 100)) {
      newErrors.low_level_threshold = 'Deve essere un numero tra 0 e 100';
    }
    
    if (formData.latitude && isNaN(parseFloat(formData.latitude))) {
      newErrors.latitude = 'Latitudine non valida';
    }
    
    if (formData.longitude && isNaN(parseFloat(formData.longitude))) {
      newErrors.longitude = 'Longitudine non valida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    // Prepara dati per invio
    const dataToSend = {
      ...formData,
      capacita_totale: formData.capacita_totale ? parseFloat(formData.capacita_totale) : null,
      current_level_liters: formData.current_level_liters ? parseFloat(formData.current_level_liters) : null,
      low_level_threshold: parseFloat(formData.low_level_threshold) || 15,
      initial_level_percent: formData.initial_level_percent ? parseFloat(formData.initial_level_percent) : null,
      initial_level_date: formData.initial_level_date || null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      azienda_id: formData.azienda_id || null,
    };
    
    mutation.mutate(dataToSend);
  };

  if (isEdit && loadingPlant) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
        <IconButton onClick={() => navigate('/crm/impianti')}>
          <BackIcon />
        </IconButton>
        {!isMobile && <GasIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
        <Typography variant={isMobile ? 'h6' : 'h4'} component="h1">
          {isEdit ? 'Modifica Impianto' : 'Nuovo Impianto'}
        </Typography>
      </Box>

      {/* Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
        <form onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Codici identificativi */}
          <Typography variant="h6" sx={{ mb: 2 }}>Identificativi</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Codice Impianto"
                value={formData.plant_code}
                onChange={handleChange('plant_code')}
                error={Boolean(errors.plant_code)}
                helperText={errors.plant_code || 'Codice univoco per associare clienti e contatori'}
                disabled={isEdit} // Non modificabile in modifica
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Codice Consegna"
                value={formData.delivery_code}
                onChange={handleChange('delivery_code')}
                helperText="Codice per associare rifornimenti da fatture XML"
              />
            </Grid>
            {aziendeData && aziendeData.length > 0 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Azienda di Fatturazione</InputLabel>
                  <Select
                    value={formData.azienda_id}
                    label="Azienda di Fatturazione"
                    onChange={(e) => setFormData(prev => ({ ...prev, azienda_id: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Predefinita</em>
                    </MenuItem>
                    {aziendeData.map((az) => (
                      <MenuItem key={az.id} value={az.id}>
                        {az.company_name || `Azienda ${az.id}`}
                        {az.is_default ? ' (Default)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome/Descrizione"
                value={formData.nome}
                onChange={handleChange('nome')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Indirizzo */}
          <Typography variant="h6" sx={{ mb: 2 }}>Indirizzo</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={formData.indirizzo}
                onChange={handleChange('indirizzo')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CAP"
                value={formData.cap}
                onChange={handleChange('cap')}
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Città"
                value={formData.citta}
                onChange={handleChange('citta')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Provincia"
                value={formData.provincia}
                onChange={handleChange('provincia')}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Coordinate GPS */}
          <Typography variant="h6" sx={{ mb: 2 }}>Coordinate GPS</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Incolla link Google Maps"
                placeholder="https://maps.google.com/... oppure https://goo.gl/maps/..."
                helperText="Incolla un link di Google Maps per estrarre automaticamente le coordinate"
                InputProps={{
                  startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  // Pattern: @lat,lng  (URL di Google Maps standard)
                  let match = val.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                  // Pattern: ?q=lat,lng o &q=lat,lng
                  if (!match) match = val.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
                  // Pattern: /place/lat,lng
                  if (!match) match = val.match(/\/place\/(-?\d+\.\d+),(-?\d+\.\d+)/);
                  // Pattern: ll=lat,lng
                  if (!match) match = val.match(/ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
                  // Pattern: coordinate dirette "lat, lng" o "lat,lng"
                  if (!match) match = val.match(/^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/);
                  if (match) {
                    setFormData(prev => ({
                      ...prev,
                      latitude: match[1],
                      longitude: match[2]
                    }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitudine"
                value={formData.latitude}
                onChange={handleChange('latitude')}
                error={Boolean(errors.latitude)}
                helperText={errors.latitude || 'Es: 45.4642'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitudine"
                value={formData.longitude}
                onChange={handleChange('longitude')}
                error={Boolean(errors.longitude)}
                helperText={errors.longitude || 'Es: 9.1900'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Capacità */}
          <Typography variant="h6" sx={{ mb: 2 }}>Capacità</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Capacità Totale (litri)"
                type="number"
                value={formData.capacita_totale}
                onChange={handleChange('capacita_totale')}
                error={Boolean(errors.capacita_totale)}
                helperText={errors.capacita_totale || 'Capacità totale in litri'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Livello */}
          <Typography variant="h6" sx={{ mb: 2 }}>Livello</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Livello Attuale (litri)"
                type="number"
                value={formData.current_level_liters}
                onChange={handleChange('current_level_liters')}
                error={Boolean(errors.current_level_liters)}
                helperText={errors.current_level_liters || (isEdit ? 'Usa "Imposta Livello" per aggiornamenti tracciati' : 'Livello iniziale')}
                disabled={isEdit} // In modifica, usare il pulsante dedicato
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Soglia Allarme (%)"
                type="number"
                value={formData.low_level_threshold}
                onChange={handleChange('low_level_threshold')}
                error={Boolean(errors.low_level_threshold)}
                helperText={errors.low_level_threshold || 'Percentuale sotto cui scatta l\'allarme'}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Livello Iniziale per Calcolo Automatico */}
          <Typography variant="h6" sx={{ mb: 2 }}>Livello Iniziale per Calcolo Automatico</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Imposta un livello iniziale dal quale calcolare automaticamente il livello attuale 
            basandosi sui rifornimenti (che aumentano) e sui consumi fatturati (che diminuiscono).
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Inizio Conteggio"
                type="date"
                value={formData.initial_level_date}
                onChange={handleChange('initial_level_date')}
                InputLabelProps={{ shrink: true }}
                helperText="Da questa data verranno considerati rifornimenti e consumi"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Percentuale Livello Iniziale"
                type="number"
                value={formData.initial_level_percent}
                onChange={handleChange('initial_level_percent')}
                inputProps={{ min: 0, max: 100 }}
                helperText={formData.initial_level_percent && formData.capacita_totale 
                  ? `Equivale a ${((parseFloat(formData.initial_level_percent) / 100) * parseFloat(formData.capacita_totale)).toFixed(0)} L su ${formData.capacita_totale} L totali`
                  : 'Percentuale del serbatoio alla data indicata'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Note e Stato */}
          <Typography variant="h6" sx={{ mb: 2 }}>Altro</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={formData.note}
                onChange={handleChange('note')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.attivo}
                    onChange={handleChange('attivo')}
                  />
                }
                label="Impianto Attivo"
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/crm/impianti')}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Salvataggio...' : (isEdit ? 'Salva Modifiche' : 'Crea Impianto')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default PlantForm;
